import { IMapRepository, MapEntity, ResponseValueObject } from '@gishub/domain';
import { BasemapRepository } from './basemap-repository';

export class MapRepository implements IMapRepository {
  static readonly #STORAGE_KEY = 'ghp_map';

  private static readonly CENTER_BOUNDS = { xmin: -180, xmax: 180, ymin: -90, ymax: 90 } as const;
  private static readonly ZOOM_RANGE = { min: 0, max: 24 } as const;

  /**
   * Zoom mínimo restaurable/persistible. Vistas por debajo de este nivel
   * (mundo entero o casi) no se restauran ni se guardan: se mantiene el
   * extent inicial del Perú (defaults).
   */
  static readonly MIN_SAVED_ZOOM = 2;

  private static isValidCenter(center: unknown): center is [number, number] {
    return (
      Array.isArray(center) &&
      center.length === 2 &&
      typeof center[0] === 'number' &&
      typeof center[1] === 'number' &&
      Number.isFinite(center[0]) &&
      Number.isFinite(center[1]) &&
      center[0] >= MapRepository.CENTER_BOUNDS.xmin &&
      center[0] <= MapRepository.CENTER_BOUNDS.xmax &&
      center[1] >= MapRepository.CENTER_BOUNDS.ymin &&
      center[1] <= MapRepository.CENTER_BOUNDS.ymax
    );
  }

  private static isValidZoom(zoom: unknown): zoom is number {
    return (
      typeof zoom === 'number' &&
      Number.isFinite(zoom) &&
      zoom >= MapRepository.ZOOM_RANGE.min &&
      zoom <= MapRepository.ZOOM_RANGE.max
    );
  }

  private static isValidRotation(rotation: unknown): rotation is number {
    return typeof rotation === 'number' && Number.isFinite(rotation);
  }

  async load(): Promise<ResponseValueObject<MapEntity>> {
    try {
      const raw = localStorage.getItem(MapRepository.#STORAGE_KEY);
      if (!raw) return await this.#defaults();
      const p = JSON.parse(raw) as Partial<MapEntity>;
      const def = (await this.#defaults()).data!;

      // Si el zoom guardado es de mundo (por debajo del mínimo) o inválido,
      // toda la vista guardada se considera inválida y se usa el extent
      // inicial del Perú.
      const savedZoom: number | undefined =
        MapRepository.isValidZoom(p.zoom) && p.zoom >= MapRepository.MIN_SAVED_ZOOM
          ? p.zoom
          : undefined;

      const hasUsableZoom = savedZoom !== undefined;

      return new ResponseValueObject(true, {
        ...def,
        center: hasUsableZoom && MapRepository.isValidCenter(p.center) ? p.center : def.center,
        zoom: savedZoom ?? def.zoom,
        rotation:
          hasUsableZoom && MapRepository.isValidRotation(p.rotation) ? p.rotation : def.rotation,
      });
    } catch {
      return await this.#defaults();
    }
  }

  async #defaults(): Promise<ResponseValueObject<MapEntity>> {
    const repo = new BasemapRepository();
    const basemapResult = await repo.getDefault();
    if (!basemapResult.success || !basemapResult.data) {
      throw new Error('No default basemap available');
    }
    const b = basemapResult.data;
    return new ResponseValueObject(true, {
      center: [-75.02, -11.5],
      zoom: 7,
      rotation: 0,
      basemap: {
        urlTemplate: b.urlTemplate,
        copyright: b.attribution,
        id: b.id,
        title: b.name,
        subDomains: [...b.subDomains],
      },
      elevation: {
        url: 'https://elevation3d.arcgis.com/arcgis/rest/services/WorldElevation3D/Terrain3D/ImageServer',
      },
      scene: {
        qualityProfile: 'high',
        atmosphereQuality: 'high',
        starsEnabled: true,
        lighting: {
          directShadowsEnabled: true,
          ambientOcclusionEnabled: true,
          date: '2024-03-15T20:00:00.000Z',
        },
      },
    });
  }

  async reset(): Promise<ResponseValueObject<void>> {
    try {
      localStorage.removeItem(MapRepository.#STORAGE_KEY);
      return new ResponseValueObject(true, undefined);
    } catch {
      return new ResponseValueObject(true, undefined);
    }
  }

  async save(view: Pick<MapEntity, 'center' | 'zoom' | 'rotation'>): Promise<ResponseValueObject<void>> {
    try {
      // No persistir vistas de mundo (zoom mínimo): se conserva la última vista válida.
      if (!MapRepository.isValidZoom(view.zoom) || view.zoom < MapRepository.MIN_SAVED_ZOOM) {
        return new ResponseValueObject(true, undefined);
      }
      localStorage.setItem(MapRepository.#STORAGE_KEY, JSON.stringify({
        center: view.center,
        zoom: view.zoom,
        rotation: view.rotation,
      }));
      return new ResponseValueObject(true, undefined);
    } catch {
      return new ResponseValueObject(true, undefined);
    }
  }
}